import {
  MessageSquare, Phone, Mic, Bot, Sparkles, Headphones,
  Volume2, Vibrate, ChevronDown, Info, Upload, X,
  type LucideIcon
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import type { LandingSection } from '@easyflow/shared';
import { type WidgetConfigState, type WidgetConfigDialogState, generateSectionId, insertAfterSection, DEFAULT_WIDGET_CONFIG } from './types';
import { getErrorMessage } from '../../../lib/utils';


interface WidgetConfigDialogProps {
  widgetConfigDialog: WidgetConfigDialogState | null;
  setWidgetConfigDialog: (v: WidgetConfigDialogState | null) => void;
  widgetConfig: WidgetConfigState;
  setWidgetConfig: React.Dispatch<React.SetStateAction<WidgetConfigState>>;
  setSections: React.Dispatch<React.SetStateAction<LandingSection[]>>;
  selectedSectionId: string | null;
}

export default function WidgetConfigDialog({
  widgetConfigDialog,
  setWidgetConfigDialog,
  widgetConfig,
  setWidgetConfig,
  setSections,
  selectedSectionId,
}: WidgetConfigDialogProps) {
  const { toast } = useToast();

  return (
      <Dialog open={!!widgetConfigDialog?.open} onOpenChange={(open) => { if (!open) setWidgetConfigDialog(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {widgetConfigDialog?.type === 'chatbot_embed' ? (
                <><MessageSquare className="h-5 w-5 text-sky-500" /> {widgetConfigDialog?.editMode ? 'Modifica' : 'Configura'} Chatbot Bubble</>
              ) : widgetConfigDialog?.type === 'gemini_voice_embed' ? (
                <><Mic className="h-5 w-5 text-blue-500" /> {widgetConfigDialog?.editMode ? 'Modifica' : 'Configura'} Gemini Voice</>
              ) : (
                <><Phone className="h-5 w-5 text-purple-500" /> {widgetConfigDialog?.editMode ? 'Modifica' : 'Configura'} Voice Agent</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {widgetConfigDialog?.type === 'chatbot_embed' ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">Colore Bubble</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={widgetConfig.bubbleColor}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, bubbleColor: e.target.value }))}
                        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
                      />
                      <Input
                        value={widgetConfig.bubbleColor}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, bubbleColor: e.target.value }))}
                        className="flex-1 text-xs font-mono"
                        maxLength={7}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">Posizione</Label>
                    <Select value={widgetConfig.bubblePosition} onValueChange={(v) => setWidgetConfig(prev => ({ ...prev, bubblePosition: v as WidgetConfigState['bubblePosition'] }))}>
                      <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bottom-right">Basso destra</SelectItem>
                        <SelectItem value="bottom-left">Basso sinistra</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium mb-1.5 block">Icona Bubble</Label>
                  <div className="flex gap-2 flex-wrap">
                    {([
                      { key: 'chat', icon: MessageSquare, label: 'Chat' },
                      { key: 'headphones', icon: Headphones, label: 'Cuffie' },
                      { key: 'mic', icon: Mic, label: 'Microfono' },
                      { key: 'phone', icon: Phone, label: 'Telefono' },
                      { key: 'sparkles', icon: Sparkles, label: 'Scintille' },
                      { key: 'bot', icon: Bot, label: 'Robot' },
                    ] as const).map(({ key, icon: Icon, label }) => (
                      <button
                        key={key}
                        onClick={() => setWidgetConfig(prev => ({ ...prev, bubbleIcon: key }))}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${widgetConfig.bubbleIcon === key ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                        title={label}
                      >
                        <Icon className="h-5 w-5" />
                        <span className="text-[10px] text-gray-500">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">Dimensione</Label>
                    <Select value={widgetConfig.bubbleSize} onValueChange={(v) => setWidgetConfig(prev => ({ ...prev, bubbleSize: v as WidgetConfigState['bubbleSize'] }))}>
                      <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="40">Piccola (40px)</SelectItem>
                        <SelectItem value="52">Media (52px)</SelectItem>
                        <SelectItem value="56">Standard (56px)</SelectItem>
                        <SelectItem value="64">Grande (64px)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">Animazione ingresso</Label>
                    <Select value={widgetConfig.animation} onValueChange={(v) => setWidgetConfig(prev => ({ ...prev, animation: v as WidgetConfigState['animation'] }))}>
                      <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bounce">Bounce</SelectItem>
                        <SelectItem value="pulse">Pulse</SelectItem>
                        <SelectItem value="slide-up">Slide Up</SelectItem>
                        <SelectItem value="none">Nessuna</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium mb-1.5 block">Messaggio Tooltip (hover)</Label>
                  <Input
                    value={widgetConfig.tooltip}
                    onChange={(e) => setWidgetConfig(prev => ({ ...prev, tooltip: e.target.value }))}
                    placeholder="Es: Chatta con noi!"
                    className="text-xs"
                    maxLength={50}
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <label className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <input
                      type="checkbox"
                      checked={widgetConfig.sound}
                      onChange={(e) => setWidgetConfig(prev => ({ ...prev, sound: e.target.checked }))}
                      className="h-3.5 w-3.5 rounded text-sky-500"
                    />
                    <div className="flex items-center gap-1">
                      <Volume2 className="h-3.5 w-3.5 text-gray-500" />
                      <span className="text-xs">Suono</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <input
                      type="checkbox"
                      checked={widgetConfig.vibration}
                      onChange={(e) => setWidgetConfig(prev => ({ ...prev, vibration: e.target.checked }))}
                      className="h-3.5 w-3.5 rounded text-sky-500"
                    />
                    <div className="flex items-center gap-1">
                      <Vibrate className="h-3.5 w-3.5 text-gray-500" />
                      <span className="text-xs">Vibra</span>
                    </div>
                  </label>
                  <div>
                    <Select value={widgetConfig.autoOpenDelay} onValueChange={(v) => setWidgetConfig(prev => ({ ...prev, autoOpenDelay: v as WidgetConfigState['autoOpenDelay'] }))}>
                      <SelectTrigger className="text-xs h-full"><SelectValue placeholder="Auto-open" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Mai</SelectItem>
                        <SelectItem value="3">3 sec</SelectItem>
                        <SelectItem value="5">5 sec</SelectItem>
                        <SelectItem value="10">10 sec</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase mb-2">Anteprima Bubble</p>
                  <div className="flex items-center justify-center py-4">
                    <div
                      className="rounded-full flex items-center justify-center shadow-lg relative"
                      style={{
                        width: `${widgetConfig.bubbleSize}px`,
                        height: `${widgetConfig.bubbleSize}px`,
                        background: widgetConfig.bubbleColor,
                        boxShadow: `0 4px 24px ${widgetConfig.bubbleColor}50`,
                      }}
                    >
                      {(() => {
                        const iconMap: Record<string, LucideIcon> = {
                          chat: MessageSquare,
                          headphones: Headphones,
                          mic: Mic,
                          phone: Phone,
                          sparkles: Sparkles,
                          bot: Bot,
                        };
                        const IconComp = iconMap[widgetConfig.bubbleIcon] || MessageSquare;
                        const iconPx = Math.round(parseInt(widgetConfig.bubbleSize) * 0.45);
                        return <IconComp style={{ width: iconPx, height: iconPx }} className="text-white" />;
                      })()}
                    </div>
                  </div>
                  {widgetConfig.tooltip && (
                    <p className="text-center text-xs text-gray-500 mt-1">"{widgetConfig.tooltip}"</p>
                  )}
                </div>
              </>
            ) : widgetConfigDialog?.type === 'gemini_voice_embed' ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">Colore Bubble</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={widgetConfig.gvColor}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, gvColor: e.target.value }))}
                        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
                      />
                      <Input
                        value={widgetConfig.gvColor}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, gvColor: e.target.value }))}
                        className="flex-1 text-xs font-mono"
                        maxLength={7}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">Posizione</Label>
                    <Select value={widgetConfig.gvPosition} onValueChange={(v) => setWidgetConfig(prev => ({ ...prev, gvPosition: v as WidgetConfigState['gvPosition'] }))}>
                      <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bottom-right">Basso destra</SelectItem>
                        <SelectItem value="bottom-left">Basso sinistra</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">Forma Bubble</Label>
                    <div className="flex gap-2">
                      {([
                        { key: 'circle' as const, label: 'Cerchio', radius: '50%' },
                        { key: 'square' as const, label: 'Quadrato', radius: '14px' },
                      ]).map(({ key, label, radius }) => (
                        <button
                          key={key}
                          onClick={() => setWidgetConfig(prev => ({ ...prev, gvShape: key }))}
                          className={`flex-1 flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${widgetConfig.gvShape === key ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                        >
                          <div
                            style={{ width: 32, height: 32, borderRadius: radius, background: widgetConfig.gvColor }}
                            className="flex items-center justify-center"
                          >
                            <Mic className="h-4 w-4 text-white" />
                          </div>
                          <span className="text-[10px] text-gray-500">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium mb-1.5 block">Testo CTA</Label>
                    <Input
                      value={widgetConfig.gvCtaText}
                      onChange={(e) => setWidgetConfig(prev => ({ ...prev, gvCtaText: e.target.value }))}
                      placeholder="Parla con noi"
                      className="text-xs"
                      maxLength={40}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase">Funzionalità</p>
                  {[
                    { key: 'gvLiveTranscript' as const, label: 'Trascrizione Live', tip: 'Mostra le parole dell\'agente in tempo reale in un pannello sotto il widget' },
                    { key: 'gvShowMute' as const, label: 'Mute Microfono', tip: 'Mostra il bottone per silenziare/riattivare il microfono durante la conversazione' },
                  ].map(({ key, label, tip }) => (
                    <div key={key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <label htmlFor={`gv-${key}`} className="text-xs font-medium cursor-pointer flex items-center gap-1.5">
                        {label}
                        <span className="relative group/tip inline-flex">
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">
                            {tip}
                            <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                          </span>
                        </span>
                      </label>
                      <button
                        id={`gv-${key}`}
                        type="button"
                        role="switch"
                        aria-checked={widgetConfig[key]}
                        onClick={() => setWidgetConfig(prev => ({ ...prev, [key]: !prev[key] }))}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${(widgetConfig[key]) ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'}`}
                      >
                        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${(widgetConfig[key]) ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  ))}
                </div>

                <details className="group">
                  <summary className="cursor-pointer text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1">
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                    Override Agente (opzionale)
                  </summary>
                  <div className="mt-3 space-y-3 pl-1">
                    <p className="text-[10px] text-gray-400">Questi override sovrascrivono i valori configurati nell'agente solo per questa landing page.</p>
                    <div>
                      <Label className="text-xs font-medium mb-1.5 block">Messaggio Iniziale Override</Label>
                      <Input
                        value={widgetConfig.gvOverrideFirstMessage}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, gvOverrideFirstMessage: e.target.value }))}
                        placeholder="Es: Ciao! Benvenuto nel nostro studio..."
                        className="text-xs"
                        maxLength={200}
                      />
                      <p className="text-[10px] text-gray-400 mt-1">Sovrascrive il saluto iniziale dell'agente</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium mb-1.5 block">Prompt Override</Label>
                      <textarea
                        value={widgetConfig.gvOverridePrompt}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, gvOverridePrompt: e.target.value }))}
                        placeholder="Es: Per questa landing sei l'assistente dello studio legale Rossi. Rispondi solo a domande legali..."
                        className="w-full text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                        maxLength={1000}
                      />
                      <p className="text-[10px] text-gray-400 mt-1">Istruzioni aggiuntive iniettate nel prompt dell'agente</p>
                    </div>
                  </div>
                </details>

                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-[10px] font-semibold text-blue-500 uppercase mb-2">Anteprima Widget</p>
                  <div className="flex items-center justify-center gap-3 py-3">
                    <div style={{ background: '#fff', color: '#333', padding: '6px 14px', borderRadius: 24, fontSize: 12, fontWeight: 500, boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
                      {widgetConfig.gvCtaText || 'Parla con noi'}
                    </div>
                    <div
                      className="flex items-center justify-center shadow-lg relative"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: widgetConfig.gvShape === 'square' ? 12 : '50%',
                        background: widgetConfig.gvColor,
                        boxShadow: `0 4px 20px ${widgetConfig.gvColor}50`,
                      }}
                    >
                      <Mic className="h-5 w-5 text-white" />
                      {widgetConfig.gvShowMute && (
                        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center">
                          <Mic className="h-2.5 w-2.5 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                  {widgetConfig.gvLiveTranscript && (
                    <div className="mt-2 bg-gray-800 text-gray-200 rounded-lg px-3 py-2 text-[11px]">
                      <span style={{ color: widgetConfig.gvColor, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Trascrizione live</span>
                      <p className="mt-1 text-gray-300 italic">Le parole appariranno qui in tempo reale...</p>
                    </div>
                  )}
                  {(() => {
                    const features = [
                      widgetConfig.gvLiveTranscript && 'Trascrizione',
                      widgetConfig.gvShowMute && 'Mute',
                      widgetConfig.gvOverrideFirstMessage && 'Msg override',
                      widgetConfig.gvOverridePrompt && 'Prompt override',
                    ].filter(Boolean);
                    return features.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-2 justify-center">
                        {features.map((f) => (
                          <span key={String(f)} className="text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded">{f}</span>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                      Posizione
                      <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Posizione del widget nella pagina<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                    </Label>
                    <Select value={widgetConfig.vaPosition} onValueChange={(v) => setWidgetConfig(prev => ({ ...prev, vaPosition: v as WidgetConfigState['vaPosition'] }))}>
                      <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bottom-right">Basso destra</SelectItem>
                        <SelectItem value="bottom-left">Basso sinistra</SelectItem>
                        <SelectItem value="top-right">Alto destra</SelectItem>
                        <SelectItem value="top-left">Alto sinistra</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                      Variante
                      <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Stile del widget: bubble compatto, pannello espanso o schermo intero<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                    </Label>
                    <Select value={widgetConfig.vaVariant || 'compact'} onValueChange={(v) => setWidgetConfig(prev => ({ ...prev, vaVariant: (v === 'compact' ? '' : v) as WidgetConfigState['vaVariant'] }))}>
                      <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="compact">Compact (bubble)</SelectItem>
                        <SelectItem value="expanded">Expanded</SelectItem>
                        <SelectItem value="fullscreen">Fullscreen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                      Lingua
                      <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Lingua dell'interfaccia e dell'agente. Richiede override abilitato nella dashboard ElevenLabs<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                    </Label>
                    <Select value={widgetConfig.vaLanguage} onValueChange={(v) => setWidgetConfig(prev => ({ ...prev, vaLanguage: v }))}>
                      <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="it">Italiano</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Español</SelectItem>
                        <SelectItem value="de">Deutsch</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="pt">Português</SelectItem>
                        <SelectItem value="nl">Nederlands</SelectItem>
                        <SelectItem value="pl">Polski</SelectItem>
                        <SelectItem value="ru">Русский</SelectItem>
                        <SelectItem value="ja">日本語</SelectItem>
                        <SelectItem value="ko">한국어</SelectItem>
                        <SelectItem value="zh">中文</SelectItem>
                        <SelectItem value="ar">العربية</SelectItem>
                        <SelectItem value="hi">हिन्दी</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {widgetConfig.vaFirstMessage && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2">
                    <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">Attenzione: Override Attivo</p>
                    <p className="text-[10px] text-amber-600 dark:text-amber-500 mt-0.5">Il campo "Messaggio Iniziale" richiede "Allow client override" abilitato nella dashboard ElevenLabs. Se non abilitato, il widget non si connetterà e mostrerà un errore.</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                    Messaggio Iniziale
                    <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Messaggio iniziale dell'agente. Se "Allow client override" non è abilitato nella dashboard ElevenLabs, il widget non funzionerà<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                  </Label>
                  <textarea
                    value={widgetConfig.vaFirstMessage}
                    onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaFirstMessage: e.target.value }))}
                    placeholder="Es: Ciao! Come posso aiutarti oggi?"
                    className="w-full text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500"
                    maxLength={200}
                  />
                  <p className="text-[10px] text-amber-500 mt-1">Richiede "Allow client override" abilitato nella dashboard ElevenLabs</p>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                      Immagine Avatar
                      <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Immagine del profilo dell'agente mostrata nel widget<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={widgetConfig.vaAvatarImage}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaAvatarImage: e.target.value }))}
                        placeholder="URL oppure carica un file..."
                        className="text-xs flex-1"
                      />
                      <label className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors whitespace-nowrap">
                        <Upload className="h-3.5 w-3.5 mr-1" />
                        Carica
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const formData = new FormData();
                              formData.append('image', file);
                              const res = await apiClient.post<{ url: string }>('/landing-pages/upload-image', formData);
                              if (res.success && res.data) {
                                const url = res.data.url;
                                setWidgetConfig(prev => ({ ...prev, vaAvatarImage: url }));
                                toast({ title: 'Avatar caricato', description: 'Immagine caricata con successo' });
                              }
                            } catch (err) {
                              toast({ title: 'Errore upload', description: getErrorMessage(err), variant: 'destructive' });
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                    {widgetConfig.vaAvatarImage && (
                      <img src={widgetConfig.vaAvatarImage} alt="Preview" className="mt-2 w-12 h-12 rounded-full object-cover border-2 border-purple-200" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                          Orb Color 1
                          <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Primo colore del gradiente dell'animazione orb intorno all'avatar<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                        </Label>
                        <Input
                          type="color"
                          value={widgetConfig.vaAvatarOrbColor1 || '#4F46E5'}
                          onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaAvatarOrbColor1: e.target.value }))}
                          className="text-xs h-9 p-1 cursor-pointer"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                          Orb Color 2
                          <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Secondo colore del gradiente dell'animazione orb intorno all'avatar<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                        </Label>
                        <Input
                          type="color"
                          value={widgetConfig.vaAvatarOrbColor2 || '#7C3AED'}
                          onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaAvatarOrbColor2: e.target.value }))}
                          className="text-xs h-9 p-1 cursor-pointer"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-5">
                      <input
                        type="checkbox"
                        id="va-show-avatar-collapsed"
                        checked={widgetConfig.vaShowAvatarCollapsed}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaShowAvatarCollapsed: e.target.checked }))}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-4 w-4"
                      />
                      <label htmlFor="va-show-avatar-collapsed" className="text-xs font-medium cursor-pointer flex items-center gap-1">
                        Avatar da chiuso
                        <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Mostra l'immagine avatar anche quando il widget è nel formato bubble chiuso<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                      </label>
                    </div>
                  </div>
                </div>

                <details className="group" open>
                  <summary className="cursor-pointer text-xs font-semibold text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center gap-1">
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                    Comportamento Widget
                  </summary>
                  <div className="mt-3 space-y-2 pl-1">
                    {[
                      { key: 'vaTextInput' as const, label: 'Input Testuale', tip: 'Abilita campo di testo per scrivere messaggi oltre alla voce' },
                      { key: 'vaMicMuting' as const, label: 'Mute Microfono', tip: 'Mostra il bottone per silenziare il microfono durante la conversazione' },
                      { key: 'vaDismissible' as const, label: 'Chiudibile', tip: 'Permette all\'utente di chiudere completamente il widget dalla pagina' },
                      { key: 'vaDefaultExpanded' as const, label: 'Aperto di default', tip: 'Il widget si apre automaticamente al caricamento della pagina' },
                      { key: 'vaAlwaysExpanded' as const, label: 'Sempre aperto', tip: 'Il widget resta sempre espanso senza poterlo chiudere o minimizzare' },
                      { key: 'vaTranscript' as const, label: 'Trascrizione', tip: 'Mostra la trascrizione testuale in tempo reale durante la conversazione vocale' },
                      { key: 'vaOverrideTextOnly' as const, label: 'Solo Testo', tip: 'Forza il widget in modalità solo testo senza audio. Richiede override abilitato nella dashboard ElevenLabs' },
                      { key: 'vaCollectFeedback' as const, label: 'Feedback', tip: 'Mostra un form di valutazione (pollice su/giù) alla fine della conversazione' },
                      { key: 'vaShowAgentStatus' as const, label: 'Stato Agente', tip: 'Mostra un indicatore di stato dell\'agente (online/offline) nel widget' },
                    ].map(({ key, label, tip }) => (
                      <div key={key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <label htmlFor={`va-${key}`} className="text-xs font-medium cursor-pointer flex items-center gap-1.5">
                          {label}
                          <span className="relative group/tip inline-flex">
                            <Info className="h-3 w-3 text-gray-400 cursor-help" />
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">
                              {tip}
                              <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
                            </span>
                          </span>
                        </label>
                        <button
                          id={`va-${key}`}
                          type="button"
                          role="switch"
                          aria-checked={widgetConfig[key]}
                          onClick={() => setWidgetConfig(prev => ({ ...prev, [key]: !prev[key] }))}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 ${(widgetConfig[key]) ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-600'}`}
                        >
                          <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${(widgetConfig[key]) ? 'translate-x-4' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </details>

                <details className="group">
                  <summary className="cursor-pointer text-xs font-semibold text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center gap-1">
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                    Testi Widget {Object.values(widgetConfig.vaTextContents).filter(v => v?.trim()).length > 0 && <span className="text-[9px] bg-purple-100 dark:bg-purple-900/30 text-purple-600 px-1.5 rounded-full">{Object.values(widgetConfig.vaTextContents).filter(v => v?.trim()).length}</span>}
                  </summary>
                  <div className="mt-3 space-y-3 pl-1">
                    <p className="text-[10px] text-gray-400 mb-1">Personalizza i testi dell'interfaccia del widget. Lascia vuoto per usare i default della lingua.</p>
                    {[
                      { cat: 'Interfaccia', items: [
                        { key: 'main_label', label: 'Titolo Bubble', placeholder: 'Serve aiuto?', def: 'Need help?' },
                        { key: 'collapse', label: 'Chiudi', placeholder: 'Chiudi', def: 'Collapse' },
                        { key: 'expand', label: 'Espandi', placeholder: 'Espandi', def: 'Expand' },
                        { key: 'change_language', label: 'Cambia Lingua', placeholder: 'Cambia lingua', def: 'Change language' },
                        { key: 'copy', label: 'Copia', placeholder: 'Copia', def: 'Copy' },
                        { key: 'copied', label: 'Copiato', placeholder: 'Copiato!', def: 'Copied!' },
                        { key: 'download', label: 'Scarica', placeholder: 'Scarica', def: 'Download' },
                      ]},
                      { cat: 'Chiamata Vocale', items: [
                        { key: 'start_call', label: 'Avvia Chiamata', placeholder: 'Inizia chiamata', def: 'Start a call' },
                        { key: 'end_call', label: 'Fine Chiamata', placeholder: 'Fine', def: 'End' },
                        { key: 'new_call', label: 'Nuova Chiamata', placeholder: 'Nuova chiamata', def: 'New call' },
                        { key: 'mute_microphone', label: 'Mute Microfono', placeholder: 'Disattiva microfono', def: 'Mute microphone' },
                        { key: 'connecting_status', label: 'Connessione', placeholder: 'Connessione...', def: 'Connecting' },
                        { key: 'listening_status', label: 'In Ascolto', placeholder: 'In ascolto', def: 'Listening' },
                        { key: 'speaking_status', label: 'Sta Parlando', placeholder: 'Parla per interrompere', def: 'Talk to interrupt' },
                      ]},
                      { cat: 'Chat Testuale', items: [
                        { key: 'start_chat', label: 'Avvia Chat', placeholder: 'Inizia chat', def: 'Start a chat' },
                        { key: 'send_message', label: 'Invia Messaggio', placeholder: 'Invia', def: 'Send' },
                        { key: 'chatting_status', label: 'Stato Chat', placeholder: 'Chat con l\'Agente AI', def: 'Chatting with AI Agent' },
                        { key: 'input_label', label: 'Label Input', placeholder: 'Input messaggio testo', def: 'Text message input' },
                        { key: 'input_placeholder', label: 'Placeholder Input', placeholder: 'Scrivi un messaggio...', def: 'Send a message...' },
                        { key: 'input_placeholder_text_only', label: 'Placeholder Solo Testo', placeholder: 'Scrivi un messaggio...', def: 'Send a message...' },
                        { key: 'input_placeholder_new_conversation', label: 'Placeholder Nuova Conv.', placeholder: 'Inizia una nuova conversazione', def: 'Start a new conversation' },
                        { key: 'text_mode', label: 'Modalità Testo', placeholder: 'Passa alla modalità testo', def: 'Switch to text mode' },
                        { key: 'voice_mode', label: 'Modalità Voce', placeholder: 'Passa alla modalità voce', def: 'Switch to voice mode' },
                        { key: 'switched_to_text_mode', label: 'Passato a Testo', placeholder: 'Modalità testo attiva', def: 'Switched to text mode' },
                        { key: 'switched_to_voice_mode', label: 'Passato a Voce', placeholder: 'Modalità voce attiva', def: 'Switched to voice mode' },
                      ]},
                      { cat: 'Stato & Errori', items: [
                        { key: 'user_ended_conversation', label: 'Utente ha Chiuso', placeholder: 'Hai terminato la conversazione', def: 'You ended the conversation' },
                        { key: 'agent_ended_conversation', label: 'Agente ha Chiuso', placeholder: 'L\'agente ha terminato la conversazione', def: 'The agent ended the conversation' },
                        { key: 'error_occurred', label: 'Errore', placeholder: 'Si è verificato un errore', def: 'An error occurred' },
                        { key: 'conversation_id', label: 'ID Conversazione', placeholder: 'ID', def: 'ID' },
                        { key: 'copy_id', label: 'Copia ID', placeholder: 'Copia ID', def: 'Copy ID' },
                        { key: 'agent_working', label: 'Agente Lavora', placeholder: 'Elaborazione...', def: 'Working...' },
                        { key: 'agent_done', label: 'Agente Finito', placeholder: 'Completato', def: 'Completed' },
                        { key: 'agent_error', label: 'Errore Agente', placeholder: 'Errore', def: 'Error occurred' },
                        { key: 'wrap', label: 'Wrap', placeholder: 'A capo', def: 'Wrap' },
                      ]},
                      { cat: 'Feedback & Termini', items: [
                        { key: 'initiate_feedback', label: 'Richiedi Feedback', placeholder: 'Com\'è andata la conversazione?', def: 'How was this conversation?' },
                        { key: 'request_follow_up_feedback', label: 'Dettagli Feedback', placeholder: 'Dicci di più', def: 'Tell us more' },
                        { key: 'thanks_for_feedback', label: 'Grazie Feedback', placeholder: 'Grazie per il tuo feedback!', def: 'Thank you for your feedback!' },
                        { key: 'thanks_for_feedback_details', label: 'Dettagli Grazie', placeholder: 'Il tuo feedback ci aiuta a migliorare.', def: 'Your feedback helps us improve our service and better support you in the future.' },
                        { key: 'follow_up_feedback_placeholder', label: 'Placeholder Feedback', placeholder: 'Raccontaci la tua esperienza...', def: 'Tell us more about your experience...' },
                        { key: 'submit', label: 'Invia', placeholder: 'Invia', def: 'Submit' },
                        { key: 'go_back', label: 'Indietro', placeholder: 'Indietro', def: 'Go back' },
                        { key: 'accept_terms', label: 'Accetta Termini', placeholder: 'Accetta', def: 'Accept' },
                        { key: 'dismiss_terms', label: 'Rifiuta Termini', placeholder: 'Annulla', def: 'Cancel' },
                      ]},
                    ].map(({ cat, items }) => (
                      <details key={cat} className="group/sub border border-gray-100 dark:border-gray-700 rounded-md">
                        <summary className="cursor-pointer text-[11px] font-medium text-gray-600 dark:text-gray-400 px-2.5 py-1.5 flex items-center justify-between">
                          <span>{cat}</span>
                          <span className="flex items-center gap-1.5">
                            {items.filter(i => widgetConfig.vaTextContents[i.key]?.trim()).length > 0 && <span className="text-[9px] bg-green-100 dark:bg-green-900/30 text-green-600 px-1.5 rounded-full">{items.filter(i => widgetConfig.vaTextContents[i.key]?.trim()).length}/{items.length}</span>}
                            <ChevronDown className="h-3 w-3 transition-transform group-open/sub:rotate-180" />
                          </span>
                        </summary>
                        <div className="px-2.5 pb-2.5 pt-1 space-y-1.5">
                          {items.map(({ key, label, placeholder, def }) => (
                            <div key={key}>
                              <Label className="text-[11px] font-medium mb-0.5 flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                {label}
                                <span className="text-[9px] text-gray-400 font-normal">({def})</span>
                              </Label>
                              <Input
                                value={widgetConfig.vaTextContents[key] || ''}
                                onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaTextContents: { ...prev.vaTextContents, [key]: e.target.value } }))}
                                placeholder={placeholder}
                                className="text-xs h-7"
                              />
                            </div>
                          ))}
                        </div>
                      </details>
                    ))}
                  </div>
                </details>

                <details className="group">
                  <summary className="cursor-pointer text-xs font-semibold text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center gap-1">
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                    Override Avanzati
                  </summary>
                  <div className="mt-3 space-y-3 pl-1">
                    <div>
                      <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                        Override Prompt
                        <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Sostituisce il prompt di sistema dell'agente. Richiede override abilitato nella dashboard ElevenLabs<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                      </Label>
                      <textarea
                        value={widgetConfig.vaOverridePrompt}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaOverridePrompt: e.target.value }))}
                        placeholder="Sostituisce il prompt dell'agente..."
                        className="w-full text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500"
                      />
                      <p className="text-[10px] text-amber-500 mt-1">Richiede override abilitato nella dashboard ElevenLabs</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                        Override Voice ID
                        <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">ID della voce ElevenLabs da usare al posto di quella configurata nell'agente<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                      </Label>
                      <Input
                        value={widgetConfig.vaOverrideVoiceId}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaOverrideVoiceId: e.target.value }))}
                        placeholder="ID voce ElevenLabs (es: EXAVITQu4vr4xnSDxMaL)"
                        className="text-xs font-mono"
                      />
                      <p className="text-[10px] text-amber-500 mt-1">Richiede override abilitato nella dashboard ElevenLabs</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                          Speed
                          <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Velocità del parlato: 0.5 = lento, 1.0 = normale, 2.0 = veloce<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                        </Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            min="0.5"
                            max="2.0"
                            step="0.1"
                            value={widgetConfig.vaOverrideSpeed || '1.0'}
                            onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaOverrideSpeed: e.target.value }))}
                            className="flex-1 h-1.5 accent-purple-600"
                          />
                          <span className="text-[10px] font-mono text-gray-500 w-7 text-right">{widgetConfig.vaOverrideSpeed || '1.0'}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                          Stability
                          <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Stabilità della voce: valori bassi = più espressiva e variabile, alti = più costante<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                        </Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={widgetConfig.vaOverrideStability || '0.5'}
                            onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaOverrideStability: e.target.value }))}
                            className="flex-1 h-1.5 accent-purple-600"
                          />
                          <span className="text-[10px] font-mono text-gray-500 w-7 text-right">{widgetConfig.vaOverrideStability || '0.5'}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                          Similarity
                          <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">Somiglianza alla voce originale: valori bassi = più libera, alti = più fedele<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                        </Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={widgetConfig.vaOverrideSimilarityBoost || '0.75'}
                            onChange={(e) => setWidgetConfig(prev => ({ ...prev, vaOverrideSimilarityBoost: e.target.value }))}
                            className="flex-1 h-1.5 accent-purple-600"
                          />
                          <span className="text-[10px] font-mono text-gray-500 w-7 text-right">{widgetConfig.vaOverrideSimilarityBoost || '0.75'}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-[10px] text-amber-500">I parametri voce richiedono override abilitato nella dashboard ElevenLabs</p>
                    <div>
                      <Label className="text-xs font-medium mb-1.5 flex items-center gap-1">
                        Variabili Dinamiche
                        <span className="relative group/tip inline-flex"><Info className="h-3 w-3 text-gray-400 cursor-help" /><span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 text-[10px] leading-tight font-normal text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg whitespace-normal w-48 text-center opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-[9999]">{"Coppie chiave-valore iniettate nella conversazione. Usa {{nome_variabile}} nel prompt dell'agente"}<span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" /></span></span>
                      </Label>
                      <p className="text-[10px] text-gray-400 mb-2">Coppie chiave-valore che l'agente può usare nella conversazione</p>
                      {widgetConfig.vaDynamicVars.map((dv, idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <Input
                            value={dv.key}
                            onChange={(e) => {
                              const updated = [...widgetConfig.vaDynamicVars];
                              updated[idx] = { ...updated[idx]!, key: e.target.value };
                              setWidgetConfig(prev => ({ ...prev, vaDynamicVars: updated }));
                            }}
                            placeholder="Nome variabile"
                            className="text-xs flex-1"
                          />
                          <Input
                            value={dv.value}
                            onChange={(e) => {
                              const updated = [...widgetConfig.vaDynamicVars];
                              updated[idx] = { ...updated[idx]!, value: e.target.value };
                              setWidgetConfig(prev => ({ ...prev, vaDynamicVars: updated }));
                            }}
                            placeholder="Valore"
                            className="text-xs flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = widgetConfig.vaDynamicVars.filter((_, i) => i !== idx);
                              setWidgetConfig(prev => ({ ...prev, vaDynamicVars: updated }));
                            }}
                            className="px-2 text-red-500 hover:text-red-700"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setWidgetConfig(prev => ({ ...prev, vaDynamicVars: [...prev.vaDynamicVars, { key: '', value: '' }] }))}
                        className="text-xs"
                      >
                        + Aggiungi variabile
                      </Button>
                    </div>
                  </div>
                </details>

                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase mb-2">Riepilogo Configurazione</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 w-24">Posizione:</span>
                      <span className="text-xs font-medium">{{ 'bottom-right': 'Basso destra', 'bottom-left': 'Basso sinistra', 'top-right': 'Alto destra', 'top-left': 'Alto sinistra' }[widgetConfig.vaPosition]}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 w-24">Variante:</span>
                      <span className="text-xs font-medium capitalize">{widgetConfig.vaVariant === 'expanded' ? 'Expanded' : widgetConfig.vaVariant === 'fullscreen' ? 'Fullscreen' : 'Compact (bubble)'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 w-24">Lingua:</span>
                      <span className="text-xs font-medium">{widgetConfig.vaLanguage.toUpperCase()}</span>
                    </div>
                    {widgetConfig.vaFirstMessage && (
                      <div className="flex items-start gap-2">
                        <span className="text-[10px] text-gray-500 w-24 shrink-0">1° Messaggio:</span>
                        <span className="text-xs text-gray-600 dark:text-gray-300 italic truncate">"{widgetConfig.vaFirstMessage}"</span>
                      </div>
                    )}
                    {widgetConfig.vaAvatarImage && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-500 w-24">Avatar:</span>
                        <img src={widgetConfig.vaAvatarImage} alt="Avatar" className="w-8 h-8 rounded-full object-cover border border-gray-200" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>
                    )}
                    {(() => {
                      const toggles = [
                        widgetConfig.vaTextInput && 'Testo',
                        widgetConfig.vaMicMuting && 'Mute',
                        widgetConfig.vaDismissible && 'Chiudibile',
                        widgetConfig.vaDefaultExpanded && 'Auto-apri',
                        widgetConfig.vaAlwaysExpanded && 'Sempre aperto',
                        widgetConfig.vaTranscript && 'Trascrizione',
                        widgetConfig.vaOverrideTextOnly && 'Solo testo',
                        widgetConfig.vaCollectFeedback && 'Feedback',
                        widgetConfig.vaShowAgentStatus && 'Stato',
                      ].filter(Boolean);
                      return toggles.length > 0 ? (
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] text-gray-500 w-24 shrink-0">Attivi:</span>
                          <div className="flex flex-wrap gap-1">
                            {toggles.map((t) => (
                              <span key={String(t)} className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded">{t}</span>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setWidgetConfigDialog(null)}>Annulla</Button>
            <Button
              className="bg-sky-500 hover:bg-sky-600 text-white"
              onClick={async () => {
                if (!widgetConfigDialog) return;
                const sectionType = widgetConfigDialog.type;
                const sectionProps: Record<string, unknown> = {
                  agentId: widgetConfigDialog.widgetId,
                  title: widgetConfigDialog.widgetName,
                };
                if (sectionType === 'chatbot_embed') {
                  Object.assign(sectionProps, {
                    bubbleColor: widgetConfig.bubbleColor,
                    bubblePosition: widgetConfig.bubblePosition,
                    bubbleIcon: widgetConfig.bubbleIcon,
                    bubbleSize: widgetConfig.bubbleSize,
                    animation: widgetConfig.animation,
                    sound: widgetConfig.sound,
                    tooltip: widgetConfig.tooltip,
                    vibration: widgetConfig.vibration,
                    autoOpenDelay: widgetConfig.autoOpenDelay,
                  });
                } else if (sectionType === 'gemini_voice_embed') {
                  Object.assign(sectionProps, {
                    color: widgetConfig.gvColor,
                    position: widgetConfig.gvPosition,
                    ctaText: widgetConfig.gvCtaText || 'Parla con noi',
                    shape: widgetConfig.gvShape,
                    liveTranscript: widgetConfig.gvLiveTranscript,
                    showMute: widgetConfig.gvShowMute,
                    overrideFirstMessage: widgetConfig.gvOverrideFirstMessage || undefined,
                    overridePrompt: widgetConfig.gvOverridePrompt || undefined,
                  });
                } else {
                  const dynVars = widgetConfig.vaDynamicVars.filter(v => v.key.trim());
                  Object.assign(sectionProps, {
                    variant: widgetConfig.vaVariant || undefined,
                    position: widgetConfig.vaPosition,
                    firstMessage: widgetConfig.vaFirstMessage || undefined,
                    avatarImage: widgetConfig.vaAvatarImage || undefined,
                    avatarOrbColor1: widgetConfig.vaAvatarOrbColor1 || undefined,
                    avatarOrbColor2: widgetConfig.vaAvatarOrbColor2 || undefined,
                    showAvatarCollapsed: widgetConfig.vaShowAvatarCollapsed || undefined,
                    language: widgetConfig.vaLanguage,
                    overridePrompt: widgetConfig.vaOverridePrompt || undefined,
                    overrideVoiceId: widgetConfig.vaOverrideVoiceId || undefined,
                    overrideSpeed: widgetConfig.vaOverrideSpeed || undefined,
                    overrideStability: widgetConfig.vaOverrideStability || undefined,
                    overrideSimilarityBoost: widgetConfig.vaOverrideSimilarityBoost || undefined,
                    textInput: widgetConfig.vaTextInput || undefined,
                    micMuting: widgetConfig.vaMicMuting || undefined,
                    dismissible: widgetConfig.vaDismissible || undefined,
                    defaultExpanded: widgetConfig.vaDefaultExpanded || undefined,
                    alwaysExpanded: widgetConfig.vaAlwaysExpanded || undefined,
                    transcript: widgetConfig.vaTranscript || undefined,
                    overrideTextOnly: widgetConfig.vaOverrideTextOnly || undefined,
                    collectFeedback: widgetConfig.vaCollectFeedback || undefined,
                    showAgentStatus: widgetConfig.vaShowAgentStatus || undefined,
                    dynamicVariables: dynVars.length > 0 ? JSON.stringify(Object.fromEntries(dynVars.map(v => [v.key.trim(), v.value]))) : undefined,
                    textContents: (() => {
                      const filled = Object.fromEntries(Object.entries(widgetConfig.vaTextContents).filter(([, v]) => v.trim()));
                      return Object.keys(filled).length > 0 ? JSON.stringify(filled) : undefined;
                    })(),
                  });

                  const needsOverrides = {
                    firstMessage: !!widgetConfig.vaFirstMessage,
                    language: !!widgetConfig.vaLanguage,
                    prompt: !!widgetConfig.vaOverridePrompt,
                    voiceId: !!widgetConfig.vaOverrideVoiceId,
                  };
                  if (needsOverrides.firstMessage || needsOverrides.prompt || needsOverrides.voiceId) {
                    try {
                      await apiClient.post(`/elevenlabs/agents/${widgetConfigDialog.widgetId}/enable-overrides`, needsOverrides);
                    } catch (err) {
                      console.warn('Enable overrides warning:', getErrorMessage(err));
                    }
                  }
                }
                if (widgetConfigDialog.editMode && widgetConfigDialog.editIndex !== undefined) {
                  setSections(prev => prev.map((s, i) => i === widgetConfigDialog.editIndex ? { ...s, props: sectionProps } : s));
                  setWidgetConfigDialog(null);
                  toast({ title: 'Widget aggiornato', description: `${widgetConfigDialog.widgetName} modificato con successo` });
                } else {
                  setSections(prev => {
                    const newSection = {
                      id: generateSectionId(sectionType, prev),
                      type: sectionType,
                      props: sectionProps,
                    };
                    return insertAfterSection(prev, newSection, selectedSectionId);
                  });
                  setWidgetConfigDialog(null);
                  toast({ title: 'Widget aggiunto', description: `${widgetConfigDialog.widgetName} configurato e aggiunto` });
                }
              }}
            >
              {widgetConfigDialog?.editMode ? 'Salva Modifiche' : 'Aggiungi Widget'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
